import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useEffect} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    PopoverClosedReason,
    type Notification,
} from "@khan/guide-notifications";
import {GuideStateApi} from "@khan/guide-state";
import type {ActionBubble} from "@khan/guide-types";

import {NotificationMessageAnchorSpan} from "./notification-message-anchor-span.tsx";

import xIcon from "@phosphor-icons/core/regular/x.svg";

type Props = Notification & {
    /**
     * The ID of the notification.
     */
    notificationId: string;
    /**
     * The name of the event that triggered the notification.
     */
    eventName: string;
    /**
     * Triggered when a CTA is clicked.
     */
    onActionSelected: (action: ActionBubble) => void;
};

/**
 * This type of notification popover displays call to actions as well as text.
 */
const NotificationMessageWithCTA = ({
    actionBubbles,
    eventName,
    notificationId,
    text,
    onActionSelected,
    onClose,
}: Props): React.ReactElement => {
    useEffect(() => {
        publishAnalyticsEventForMinorConversion({
            aiGuideNavigationId: GuideStateApi.latest().navigation.id,
            aiGuideNotificationId: notificationId,
            aiGuideNotificationEvent: eventName,
            eventSchemaVersion: 2,
            eventType: "AiGuideNotificationPopoverOpened",
        });
        // We only want this to fire whenever a new notification is shown
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting from old code.
    }, [notificationId]);

    const handleClose = useCallback(
        (reason: PopoverClosedReason) => {
            publishAnalyticsEventForMinorConversion({
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
                eventSchemaVersion: 2,
                eventType: "AiGuideNotificationPopoverDismissed",
                aiGuideNotificationId: notificationId,
            });
            onClose(reason);
        },
        [notificationId, onClose],
    );

    return (
        <Popover
            placement="top"
            opened
            dismissEnabled={true}
            onClose={() => {
                handleClose(PopoverClosedReason.autoDismissed);
            }}
            children={<NotificationMessageAnchorSpan />}
            showTail={false}
            content={
                <PopoverContentCore>
                    <IconButton
                        onClick={() => {
                            handleClose(PopoverClosedReason.userDismissed);
                        }}
                        icon={xIcon}
                        style={styles.closeButton}
                        kind="tertiary"
                        actionType="neutral"
                        aria-label={t`Close`}
                    />
                    <View style={styles.textWrapper}>
                        <Body>{text}</Body>
                    </View>
                    {actionBubbles && actionBubbles.length > 0 && (
                        <View style={styles.actions}>
                            {actionBubbles.map((action) => (
                                <Button
                                    key={action.command}
                                    kind="primary"
                                    onClick={() => {
                                        onActionSelected(action);
                                        handleClose(
                                            PopoverClosedReason.ctaClicked,
                                        );
                                    }}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </View>
                    )}
                </PopoverContentCore>
            }
        />
    );
};

const styles = StyleSheet.create({
    closeButton: {
        margin: 0,
        position: "absolute",
        insetInlineEnd: spacing.xxxSmall_4,
        top: spacing.xxxSmall_4,
        // Allows the button to be above the title and/or custom content
        zIndex: 1,
    },
    textWrapper: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.small_12,
    },
    actions: {
        marginBlockStart: spacing.large_24,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
    },
});

export {NotificationMessageWithCTA};
